import React from 'react';
import type {Record} from 'immutable';
import {Fieldset, Text} from '@albert-io/atomic';
import {useShallow} from 'zustand/react/shallow';
import cx from 'classnames';

import {callTargetedAction, setUpStore} from 'client/framework';
import MultipleChoiceQuestionStore from 'client/components/QuestionTypes/MultipleChoice/MultipleChoiceQuestion.store';
import {OptionEliminatorActions} from 'components/PracticeView/OptionEliminator/OptionEliminator.react';
import DraftGuessWrapper from 'client/AssignmentPracticeView/DraftGuesses/DraftGuessWrapper';

import {useQuestionTranslationStore, translatedInstructions} from '../shared/TranslationSection';

import multipleChoiceQuestionActions from './MultipleChoiceQuestion.actions';
import MultipleChoiceOption from './MultipleChoiceOption.react';
import OptionEliminatorInputWrapper from './MCQOptionEliminator/MCQOptionEliminator.react';
import {useMathJaxOverflow} from './useMathJaxOverflow';

import './multiple-choice-question.scss';

interface Props {
  isGuessSubmitted: boolean;
  question: Record<string, any>;
  storeName?: string;
  isMCQRestylingEnabled: boolean;
}

function MultipleChoiceInputArea({
  isGuessSubmitted,
  storeName = 'multipleChoiceQuestionStore',
  question,
  isMCQRestylingEnabled
}: Props) {
  const isSelectMultiple = question.isSelectMultiple();
  const store = setUpStore(MultipleChoiceQuestionStore, storeName, question.getId(), question);
  const {ref: mathJaxOverflowRef, hasMathJaxOverflow} = useMathJaxOverflow(isMCQRestylingEnabled);

  /**
   * @todo: Having stores hold onto the rubric doesn't make sense since it's available
   * on the question. We should update other types to do this as well. There's probably
   * more that we can be pulling out of the question directly, rathen than writing these
   * things to the store.
   */
  const rubric = question.getMeta().getQuestionRubric();
  const correctOptions = rubric ? rubric.get('validResponse') : null;

  // Translation info

  const {translatedQuestionInfo, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  return (
    <DraftGuessWrapper storeName={storeName} submitted={isGuessSubmitted}>
      <div className='u-mar-b_4'>
        {!isGuessSubmitted && !isMCQRestylingEnabled && <OptionEliminatorActions />}
        <div className='mcq__options-wrapper'>
          <Fieldset>
            {!showTranslations && !isGuessSubmitted && (
              <Text as='legend' size='m' className='u-mar-b_2' bold>
                {isSelectMultiple ? 'Select all that apply' : 'Select one answer'}
              </Text>
            )}
            {showTranslations && !isGuessSubmitted && (
              <Text as='legend' size='m' className='u-mar-b_2' bold>
                {isSelectMultiple
                  ? translatedInstructions.selectMultiple
                  : translatedInstructions.selectOne}
              </Text>
            )}
            <div
              ref={mathJaxOverflowRef}
              className={cx({
                'mcq__options-grid': isMCQRestylingEnabled,
                'mcq__options-grid--mathjax-overflow': hasMathJaxOverflow
              })}
            >
              {question.getOptions().map((option, index) => {
                const optionId = option.getId();
                const translatedText: string =
                  translatedQuestionInfo?.options[index]?.value || option.getValue();

                const handleEliminate = () => {
                  callTargetedAction({
                    name: multipleChoiceQuestionActions.REMOVE_OPTION_FROM_SELECTED,
                    payload: optionId,
                    targetStore: storeName
                  });
                };

                if (isMCQRestylingEnabled) {
                  // Direct rendering with built-in eliminator when restyling is enabled
                  return (
                    <MultipleChoiceOption
                      key={optionId}
                      index={index}
                      option={option}
                      questionId={question.getId()}
                      isGuessSubmitted={isGuessSubmitted}
                      isSelected={store.isSelectedOption(optionId)}
                      isCorrectAnswer={correctOptions && correctOptions.has(optionId)}
                      isEliminated={false} // Will be handled by context
                      targetStore={store.getName()}
                      isSelectMultiple={isSelectMultiple}
                      translatedOptionText={translatedText}
                      showTranslation={showTranslations}
                      isMCQRestylingEnabled={isMCQRestylingEnabled}
                      onEliminate={handleEliminate}
                    />
                  );
                }

                // Legacy rendering with wrapper
                return (
                  <OptionEliminatorInputWrapper
                    questionId={question.getId()}
                    optionId={optionId}
                    disabled={isGuessSubmitted}
                    onEliminate={handleEliminate}
                    key={optionId}
                  >
                    {(isEliminated) => (
                      <MultipleChoiceOption
                        index={index}
                        option={option}
                        questionId={question.getId()}
                        isGuessSubmitted={isGuessSubmitted}
                        isSelected={store.isSelectedOption(optionId)}
                        isCorrectAnswer={correctOptions && correctOptions.has(optionId)}
                        isEliminated={isEliminated}
                        targetStore={store.getName()}
                        isSelectMultiple={isSelectMultiple}
                        translatedOptionText={translatedText}
                        showTranslation={showTranslations}
                      />
                    )}
                  </OptionEliminatorInputWrapper>
                );
              })}
            </div>
          </Fieldset>
        </div>
      </div>
    </DraftGuessWrapper>
  );
}

export {MultipleChoiceInputArea};
